import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import HearingRoundedIcon from '@mui/icons-material/HearingRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { CharacterGrade, FeedbackLanguageItem, OpenResponseFeedbackPreview, PronunciationTip } from '../../../student/mockLessonContent';
import { SoundIndicator } from '../../../student/components/exercises/PronunciationDetail';
import { BLUE, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, RING_TRACK, SLATE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_SOFT, YELLOW } from '../../../student/studentStyles';
import { useExerciseLayout } from './exerciseLayout';

interface Props {
    preview: OpenResponseFeedbackPreview;
    submitted: boolean;
    // Moves the student to the answer field (or recorder) to revise.
    onRevise: () => void;
}

const sourceHeadings: Record<OpenResponseFeedbackPreview['source'], string> = {
    writing: 'Your answer',
    script: 'Your answer, as you said it',
    transcript: 'What we heard',
};

const strengthStyles: Record<PronunciationTip['strength'], { label: string; color: string }> = {
    likely: { label: 'Work on this', color: RED },
    possible: { label: 'Try this', color: YELLOW },
};

const sectionTitleSx = { fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY } as const;
const bodySx = { fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: NAVY } as const;
const noteSx = { fontFamily: INTER, fontSize: 12, lineHeight: 1.5, color: SLATE } as const;

// The live app plays this part of the student's recording; the preview says so
// when pressed, since the sample answer has no audio.
function ClipButton({ clip }: { clip: string }) {
    const [pressed, setPressed] = React.useState(false);
    return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Box component="button" type="button" onClick={() => setPressed(true)} aria-label={`Play recording from ${clip}`}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', minHeight: 44, px: 1, borderRadius: '999px', border: `1px solid ${RING_TRACK}`, bgcolor: '#fff', cursor: 'pointer', fontFamily: INTER, fontWeight: 700, fontSize: 11, color: BLUE, whiteSpace: 'nowrap', '&:hover': { borderColor: BLUE }, '&:focus-visible': { outline: `3px solid ${BLUE}`, outlineOffset: 1 } }}>
            <PlayArrowRoundedIcon aria-hidden="true" sx={{ fontSize: 14 }} />{clip}
        </Box>
        {pressed && <Typography component="span" role="status" sx={{ ...noteSx, fontSize: 11 }}>Preview: plays your recording from {clip} in the live app.</Typography>}
    </Box>;
}

interface SpanAnchor {
    sentence: number;
    start: number;
}

// Where a language item's span sits in the answer: its occurrence-th match (0-based),
// counting through the sentences in order, so repeated wording is not misplaced.
function findAnchor(sentences: { text: string }[], span: string, occurrence: number): SpanAnchor | null {
    let remaining = occurrence;
    for (let sentence = 0; sentence < sentences.length; sentence++) {
        for (let start = sentences[sentence].text.indexOf(span); start >= 0; start = sentences[sentence].text.indexOf(span, start + 1)) {
            if (remaining-- === 0) return { sentence, start };
        }
    }
    return null;
}

// The sentence with each language item's span underlined and numbered. Overlapping
// spans share one underline that carries all of their numbers.
function AnnotatedText({ text, items }: { text: string; items: { start: number; end: number; number: number }[] }) {
    const groups: { start: number; end: number; numbers: number[] }[] = [];
    for (const item of [...items].sort((a, b) => a.start - b.start || a.number - b.number)) {
        const last = groups[groups.length - 1];
        if (last && item.start < last.end) {
            last.end = Math.max(last.end, item.end);
            last.numbers.push(item.number);
        } else {
            groups.push({ start: item.start, end: item.end, numbers: [item.number] });
        }
    }
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    for (const group of groups) {
        if (group.start > cursor) parts.push(text.slice(cursor, group.start));
        const chars = Array.from(text.slice(group.start, group.end));
        const label = group.numbers.length > 1 ? `language notes ${group.numbers.join(' and ')}` : `language note ${group.numbers[0]}`;
        // The last character stays on the same line as its note numbers.
        parts.push(<Box component="span" key={group.start} sx={{ textDecoration: 'underline', textDecorationColor: BLUE, textDecorationThickness: '2px', textUnderlineOffset: '4px' }}>
            {chars.slice(0, -1).join('')}<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {chars[chars.length - 1]}<Box component="sup" aria-label={label} sx={{ ml: '1px', fontFamily: INTER, fontWeight: 700, fontSize: 10, color: BLUE }}>{group.numbers.join(',')}</Box>
            </Box>
        </Box>);
        cursor = group.end;
    }
    parts.push(text.slice(cursor));
    return <Typography lang="zh" sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 18, lineHeight: 1.7, color: NAVY }}>{parts}</Typography>;
}

// Compact per-character tiles: hanzi, target pinyin, heard pinyin when it differs,
// and the initial / final / tone indicators used on the pronunciation page. A
// sound that was not heard gets the pronunciation page's dashed outline, no bars.
function CompactTiles({ grades }: { grades: CharacterGrade[] }) {
    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {grades.map((grade, index) => {
            const alignment = grade.alignment ?? 'speech';
            const differs = alignment === 'speech' && grade.pinyinSpoken !== grade.pinyinExpected;
            return <Box key={`${grade.character}-${index}`} sx={{
                minWidth: 44, px: 0.5, py: 0.5, borderRadius: '10px', bgcolor: SURFACE_SOFT, textAlign: 'center',
                border: alignment === 'speech' ? 'none' : `2px dashed ${alignment === 'silence' ? RED : SLATE}`,
            }}>
                <Typography lang="zh" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, lineHeight: 1.2, color: NAVY }}>{grade.character}</Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>{grade.pinyinExpected}</Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, fontWeight: 700, color: alignment === 'speech' ? ERROR_TEXT : SLATE, minHeight: 16 }}>
                    {alignment === 'silence' ? 'not heard' : alignment === 'extra' ? 'extra' : differs ? grade.pinyinSpoken : ''}
                </Typography>
                {alignment === 'speech' && <Box sx={{ display: 'flex', gap: '2px', mt: 0.25 }}>
                    {([['initial', grade.initial], ['final', grade.final], ['tone', grade.tone]] as const).map(([part, value]) => (
                        <SoundIndicator key={part} part={part} value={value} />
                    ))}
                </Box>}
            </Box>;
        })}
    </Box>;
}

function SoundMeter({ label, percent }: { label: string; percent: number }) {
    const color = percent >= 90 ? GREEN : percent >= 75 ? YELLOW : RED;
    return <Box sx={{ flex: 1, minWidth: 120 }}>
        <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>{label}</Typography>
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>{percent}%</Typography>
        </Stack>
        <Box role="img" aria-label={`${label}: ${percent}% clear`} sx={{ mt: 0.5, height: 8, borderRadius: '999px', bgcolor: RING_TRACK, overflow: 'hidden' }}>
            <Box sx={{ width: `${percent}%`, height: '100%', bgcolor: color }} />
        </Box>
    </Box>;
}

interface DiffPart {
    kind: 'same' | 'removed' | 'added';
    text: string;
}

// Character-level diff (longest common subsequence) from the learner's sentence to
// the revised one; at a replacement the removed text comes before the added text.
function diffChars(before: string, after: string): DiffPart[] {
    const a = Array.from(before);
    const b = Array.from(after);
    const common = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            common[i][j] = a[i] === b[j] ? common[i + 1][j + 1] + 1 : Math.max(common[i + 1][j], common[i][j + 1]);
        }
    }
    const parts: DiffPart[] = [];
    const push = (kind: DiffPart['kind'], text: string) => {
        const last = parts[parts.length - 1];
        if (last && last.kind === kind) last.text += text;
        else parts.push({ kind, text });
    };
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) {
            push('same', a[i++]);
            j++;
        } else if (i < a.length && (j === b.length || common[i + 1][j] >= common[i][j + 1])) {
            push('removed', a[i++]);
        } else {
            push('added', b[j++]);
        }
    }
    return parts;
}

// The learner's sentence with this change applied: removed text struck through,
// added text highlighted.
function SentenceChange({ before, after }: { before: string; after: string }) {
    return <Typography lang="zh" sx={{ mt: 0.25, ...bodySx, fontSize: 15 }}>
        {diffChars(before, after).map((part, index) => {
            if (part.kind === 'removed') {
                return <Box component="del" key={index} sx={{ color: ERROR_TEXT, textDecorationThickness: '1.5px' }}>{part.text}</Box>;
            }
            if (part.kind === 'added') {
                return <Box component="ins" key={index} sx={{ px: '1px', borderRadius: '3px', bgcolor: SUCCESS_SOFT, color: SUCCESS_TEXT, fontWeight: 700, textDecoration: 'none' }}>{part.text}</Box>;
            }
            return <React.Fragment key={index}>{part.text}</React.Fragment>;
        })}
    </Typography>;
}

function LanguageItem({ item, number, sentence }: { item: FeedbackLanguageItem; number: number; sentence?: string }) {
    return <Stack direction="row" spacing={1.25} alignItems="flex-start" component="li">
        <Box aria-hidden="true" sx={{ flexShrink: 0, width: 22, height: 22, mt: '1px', borderRadius: '50%', bgcolor: BLUE, color: '#fff', display: 'grid', placeItems: 'center', fontFamily: INTER, fontWeight: 700, fontSize: 12 }}>{number}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.06em', color: item.tier === 'correction' ? BLUE : SLATE }}>
                {item.tier === 'correction' ? 'A CORRECTION' : 'ANOTHER WAY TO SAY IT'}
            </Typography>
            {sentence ? <SentenceChange before={sentence} after={item.revised} />
                : <Typography lang="zh" sx={{ mt: 0.25, ...bodySx, fontSize: 15 }}>
                    <Box component="span" sx={{ color: SLATE }}>{item.span}</Box>
                    {' → '}<Box component="span" sx={{ fontWeight: 600 }}>{item.revised}</Box>
                </Typography>}
            <Typography sx={{ mt: 0.5, ...bodySx }}>{item.explanation}</Typography>
            {item.exampleZh && <Typography sx={{ mt: 0.5, ...noteSx }}>
                Example: <Box component="span" lang="zh" sx={{ color: NAVY }}>{item.exampleZh}</Box> — {item.exampleEn}
            </Typography>}
        </Box>
    </Stack>;
}

export default function OpenResponseFeedbackPanel({ preview, submitted, onRevise }: Props) {
    const desktop = useExerciseLayout() === 'desktop';
    const [practiceNote, setPracticeNote] = React.useState(false);
    // Character tiles stay collapsed so revising does not mean scrolling past them.
    const [showTiles, setShowTiles] = React.useState(false);
    const tilesId = React.useId();
    const graded = preview.sentences.flatMap(sentence => sentence.grades ?? []).filter(grade => (grade.alignment ?? 'speech') === 'speech');
    const share = (part: 'initial' | 'final' | 'tone') =>
        graded.length ? Math.round(100 * graded.filter(grade => grade[part] === 'good').length / graded.length) : 0;
    const anchors = preview.language.map(item => findAnchor(preview.sentences, item.span, item.occurrence ?? 0));
    const anchoredBySentence = preview.sentences.map((_, sentenceIndex) => preview.language.flatMap((item, index) => {
        const anchor = anchors[index];
        return anchor && anchor.sentence === sentenceIndex
            ? [{ start: anchor.start, end: anchor.start + item.span.length, number: index + 1 }] : [];
    }));
    const hasSound = graded.length > 0;

    return <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <RateReviewRoundedIcon aria-hidden="true" sx={{ color: BLUE, fontSize: 22, mt: 0.25 }} />
            <Box>
                <Box component="span" sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '999px', bgcolor: `${YELLOW}59`, fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.06em', color: NAVY }}>EXAMPLE</Box>
                <Typography component="h3" sx={{ mt: 0.75, fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Feedback on a sample answer</Typography>
                <Typography sx={{ mt: 0.5, ...bodySx }}>
                    {submitted ? 'This was example feedback shown before you finished.'
                        : 'This preview shows the same scripted feedback every time. It does not review what you wrote or recorded.'}
                </Typography>
            </Box>
        </Stack>

        <Box sx={{ p: 1.75, borderRadius: '14px', bgcolor: '#fff', border: `1px solid ${RING_TRACK}` }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <TrackChangesRoundedIcon aria-hidden="true" sx={{ fontSize: 19, color: BLUE }} />
                <Typography component="h4" sx={sectionTitleSx}>Focus next time</Typography>
            </Stack>
            <Stack component="ol" spacing={0.75} sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {preview.focus.map(item => <Typography component="li" key={item} lang="zh" sx={bodySx}>{item}</Typography>)}
            </Stack>
            {!submitted && <Button onClick={onRevise} startIcon={<EditOutlinedIcon />}
                sx={{ mt: 1.25, minHeight: 44, px: 1.25, borderRadius: '999px', border: `1px solid ${RING_TRACK}`, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                Revise your answer
            </Button>}
        </Box>

        {hasSound && <Box>
            <Stack direction="row" spacing={1} alignItems="center">
                <GraphicEqRoundedIcon aria-hidden="true" sx={{ fontSize: 19, color: BLUE }} />
                <Typography component="h4" sx={sectionTitleSx}>Sounds in this recording</Typography>
            </Stack>
            <Stack direction={desktop ? 'row' : 'column'} spacing={desktop ? 2 : 1.25} sx={{ mt: 1.25 }}>
                <SoundMeter label="Initials" percent={share('initial')} />
                <SoundMeter label="Finals" percent={share('final')} />
                <SoundMeter label="Tones" percent={share('tone')} />
            </Stack>
            <Typography sx={{ mt: 0.75, ...noteSx }}>Share of graded sounds that were clear. Unclear parts of the recording are not counted.</Typography>
        </Box>}

        <Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography component="h4" sx={sectionTitleSx}>{sourceHeadings[preview.source]}</Typography>
                {hasSound && <Button onClick={() => setShowTiles(open => !open)} aria-expanded={showTiles} aria-controls={tilesId}
                    endIcon={showTiles ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    sx={{ minHeight: 44, px: 1, color: BLUE, fontFamily: INTER, fontWeight: 700, fontSize: 13, textTransform: 'none' }}>
                    {showTiles ? 'Hide sound details' : 'Show sound details'}
                </Button>}
            </Stack>
            {preview.source === 'script' && <Typography sx={{ mt: 0.5, ...noteSx }}>
                Your writing, compared with your recording. Where you used different words, the sounds are graded as you said them.
            </Typography>}
            {preview.source === 'transcript' && <Typography sx={{ mt: 0.5, ...noteSx }}>
                An automatic transcript of your recording. It may not match exactly what you said; the sound details show what the computer heard.
            </Typography>}
            <Stack id={tilesId} spacing={1.75} sx={{ mt: 1.25 }}>
                {preview.sentences.map((sentence, index) => <Box key={sentence.text} sx={{ pb: 1.5, borderBottom: index < preview.sentences.length - 1 ? `1px solid ${RING_TRACK}` : 'none' }}>
                    {sentence.clip && <ClipButton clip={sentence.clip} />}
                    <AnnotatedText text={sentence.text} items={anchoredBySentence[index]} />
                    {sentence.heardDifferently && <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.75, p: 1, borderRadius: '10px', bgcolor: `${YELLOW}26` }}>
                        <HearingRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: NAVY, mt: '1px' }} />
                        <Typography lang="zh" sx={{ ...noteSx, color: NAVY }}>{sentence.heardDifferently}</Typography>
                    </Stack>}
                    {sentence.unclear && <Typography sx={{ mt: 0.5, ...noteSx }}>
                        Unclear in the recording, so this sentence is not graded. Listen back to check what you said.
                    </Typography>}
                    {showTiles && sentence.grades && <CompactTiles grades={sentence.grades} />}
                </Box>)}
            </Stack>
            {showTiles && <Typography sx={{ mt: 1, ...noteSx }}>
                Tiles: target pinyin, then what we heard when it differs. Bars: initial · final · tone (✓ Good, ~ Close, ! Needs practice). A dashed outline marks a sound we did not hear.
            </Typography>}
        </Box>

        <Box>
            <Typography component="h4" sx={sectionTitleSx}>Language ({preview.language.length})</Typography>
            {preview.source === 'transcript' && <Typography sx={{ mt: 0.5, ...noteSx }}>Based on the transcript above, so check that it matches what you meant.</Typography>}
            <Stack component="ol" spacing={1.75} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
                {preview.language.map((item, index) => <LanguageItem key={item.span} item={item} number={index + 1}
                    sentence={anchors[index] ? preview.sentences[anchors[index].sentence].text : undefined} />)}
            </Stack>
        </Box>

        {(preview.patterns?.length || preview.tips?.length) ? <Box>
            <Typography component="h4" sx={sectionTitleSx}>Pronunciation</Typography>
            {preview.patterns && preview.patterns.length > 0 && <Box sx={{ mt: 1.25, p: 1.5, borderRadius: '12px', bgcolor: SURFACE_SOFT }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: SLATE }}>PATTERNS IN THIS ANSWER</Typography>
                <Stack component="ul" spacing={1} sx={{ m: 0, mt: 1, p: 0, listStyle: 'none' }}>
                    {preview.patterns.map(pattern => <Stack component="li" key={pattern.label} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ ...bodySx, fontWeight: 600 }}>{pattern.label}</Typography>
                        <Typography sx={noteSx}>{pattern.count} times</Typography>
                        <Typography lang="zh" sx={noteSx}>e.g. {pattern.examples.join(', ')}</Typography>
                    </Stack>)}
                </Stack>
                <Button onClick={() => setPracticeNote(open => !open)} sx={{ mt: 1, minHeight: 44, px: 1, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                    Practice these sounds
                </Button>
                {practiceNote && <Typography role="status" sx={noteSx}>In the live app this asks which sentences to add to your Pronunciation practice list, in their corrected wording.</Typography>}
            </Box>}
            {preview.tips && preview.tips.length > 0 && <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                {preview.tips.map(tip => {
                    const style = strengthStyles[tip.strength];
                    return <Box key={tip.word} sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${RING_TRACK}`, borderLeft: `4px solid ${style.color}`, bgcolor: '#fff' }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.06em', color: NAVY }}>{style.label.toUpperCase()}</Typography>
                            <Typography lang="zh" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>{tip.word}</Typography>
                            <Typography sx={noteSx}>target <Box component="span" sx={{ color: NAVY, fontWeight: 600 }}>{tip.expected}</Box> · heard <Box component="span" sx={{ color: ERROR_TEXT, fontWeight: 700 }}>{tip.heard}</Box></Typography>
                            {tip.clip && <ClipButton clip={tip.clip} />}
                        </Stack>
                        <Typography lang="zh" sx={{ mt: 0.75, ...bodySx }}>{tip.tip}</Typography>
                    </Box>;
                })}
            </Stack>}
        </Box> : null}
    </Stack>;
}
